import React, { useState } from 'react';
import { CheckCircle2Icon, AlertTriangleIcon, SparklesIcon, PlayIcon, UsersIcon, MessageSquareWarningIcon, ChevronDownIcon, ChevronUpIcon, RefreshCwIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
interface WorkerResult {
  name: string;
  role: string;
  score: number;
  total: number;
  status: 'good' | 'partial' | 'poor';
  missedQuestion?: string;
  concern?: string;
}
const CREW_RESULTS: WorkerResult[] = [{
  name: 'Mike',
  role: 'Foreman',
  score: 3,
  total: 3,
  status: 'good'
}, {
  name: 'Dan',
  role: 'Crane Operator',
  score: 3,
  total: 3,
  status: 'good'
}, {
  name: 'Luis',
  role: 'Rigger',
  score: 3,
  total: 3,
  status: 'good'
}, {
  name: 'Jake',
  role: 'Ironworker',
  score: 2,
  total: 3,
  status: 'partial',
  missedQuestion: 'What is the correct response if wind speed increases during the lift?'
}];
const statusConfig = {
  good: {
    label: 'Good',
    bg: 'bg-green-500/10',
    border: 'border-green-500/20',
    text: 'text-green-400',
    icon: CheckCircle2Icon,
    iconColor: 'text-green-500',
    dot: 'bg-green-500'
  },
  partial: {
    label: 'Partial',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    text: 'text-amber-400',
    icon: AlertTriangleIcon,
    iconColor: 'text-amber-500',
    dot: 'bg-amber-500'
  },
  poor: {
    label: 'Poor',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    text: 'text-red-400',
    icon: AlertTriangleIcon,
    iconColor: 'text-red-500',
    dot: 'bg-red-500'
  }
};
export function Screen18_CrewQuizResults({
  onBack,
  onNext
}: ScreenProps) {
  const [expandedCard, setExpandedCard] = useState<string | null>('Jake');
  const goodCount = CREW_RESULTS.filter(r => r.status === 'good').length;
  const totalCount = CREW_RESULTS.length;
  return <div className="flex flex-col h-full" data-id="element-731">
      <div className="mb-5" data-id="element-732">
        <h2 className="text-2xl font-bold text-white mb-1" data-id="element-733">
          Crew Understanding Status
        </h2>
        <p className="text-slate-400 text-sm" data-id="element-734">
          Quiz results for the Pre-Task Plan safety briefing.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-5" data-id="element-735">
        {/* Project Context */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-736">
          <div className="grid grid-cols-2 gap-3" data-id="element-737">
            <div data-id="element-738">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-739">
                Project
              </p>
              <p className="text-white font-medium text-sm" data-id="element-740">
                Downtown Office Tower
              </p>
            </div>
            <div data-id="element-741">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-742">
                Location
              </p>
              <p className="text-white font-medium text-sm" data-id="element-743">
                Level 2 Structural Frame
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800" data-id="element-744">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-745">
              Task
            </p>
            <p className="text-orange-400 font-bold text-sm" data-id="element-746">
              Steel Beam Installation Using Mobile Crane
            </p>
          </div>
        </div>

        {/* Crew Readiness Summary Bar */}
        <div className="flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3" data-id="element-747">
          <UsersIcon className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-748" />
          <div className="flex-1" data-id="element-749">
            <div className="flex items-center justify-between mb-1.5" data-id="element-750">
              <span className="text-xs font-bold text-slate-300 uppercase tracking-wider" data-id="element-751">
                Crew Readiness
              </span>
              <span className="text-xs font-bold text-white" data-id="element-752">
                {goodCount}/{totalCount} Good
              </span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2 flex overflow-hidden gap-0.5" data-id="element-753">
              {CREW_RESULTS.map((r, i) => <div key={i} className={`h-2 flex-1 rounded-full ${statusConfig[r.status].dot}`} data-id="element-754" />)}
            </div>
          </div>
        </div>

        {/* Crew Result Cards */}
        <section data-id="element-755">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-756">
            Crew Quiz Results
          </h3>
          <div className="space-y-2.5" data-id="element-757">
            {CREW_RESULTS.map(worker => {
            const config = statusConfig[worker.status];
            const StatusIcon = config.icon;
            const isExpanded = expandedCard === worker.name;
            const hasDetails = worker.missedQuestion || worker.concern;
            return <motion.div key={worker.name} layout className={`${config.bg} border ${config.border} rounded-xl overflow-hidden`} data-id="element-758">
                  <button onClick={() => hasDetails && setExpandedCard(isExpanded ? null : worker.name)} className="w-full px-4 py-3 flex items-center gap-3 text-left" data-id="element-759">
                    <StatusIcon className={`w-5 h-5 ${config.iconColor} flex-shrink-0`} data-id="element-760" />
                    <div className="flex-1 min-w-0" data-id="element-761">
                      <div className="flex items-center gap-2" data-id="element-762">
                        <span className="text-white font-semibold text-sm" data-id="element-763">
                          {worker.name}
                        </span>
                        <span className="text-slate-500 text-xs" data-id="element-764">·</span>
                        <span className="text-slate-400 text-xs" data-id="element-765">
                          {worker.role}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5" data-id="element-766">
                        <span className="text-slate-300 text-xs font-medium" data-id="element-767">
                          {worker.score} / {worker.total}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text}`} data-id="element-768">
                          {config.label}
                        </span>
                      </div>
                    </div>
                    {hasDetails && <div className="text-slate-500 flex-shrink-0" data-id="element-769">
                        {isExpanded ? <ChevronUpIcon className="w-4 h-4" data-id="element-770" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-771" />}
                      </div>}
                  </button>

                  <AnimatePresence data-id="element-772">
                    {isExpanded && hasDetails && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-773">
                        <div className="px-4 pb-3 space-y-2.5 border-t border-slate-800/50 pt-2.5" data-id="element-774">
                          {worker.missedQuestion && <div data-id="element-775">
                              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-776">
                                Missed Question
                              </p>
                              <p className="text-amber-200 text-sm" data-id="element-777">
                                {worker.missedQuestion}
                              </p>
                            </div>}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </section>

        {/* AI Crew Readiness Analysis */}
        <section data-id="element-778">
          <div className="flex items-center gap-2 mb-3" data-id="element-779">
            <SparklesIcon className="w-5 h-5 text-orange-500" data-id="element-780" />
            <h3 className="text-xs font-bold text-orange-400 uppercase tracking-wider" data-id="element-781">
              AI Crew Readiness Analysis
            </h3>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 space-y-3" data-id="element-782">
            <p className="text-orange-100 text-sm leading-relaxed" data-id="element-783">
              Most crew members demonstrate good understanding of the task and
              hazards. Jake missed a question about wind response procedures
              during the lift.
            </p>
            <div className="bg-slate-950/40 rounded-lg p-3" data-id="element-784">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-785">
                Suggested Action
              </p>
              <p className="text-white text-sm font-medium" data-id="element-786">
                Briefly review wind response procedures with Jake before
                starting the lift.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-1" data-id="element-787">
              <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-788" />
              <span className="text-amber-400 text-xs font-bold uppercase tracking-wider" data-id="element-789">
                Ready with minor review
              </span>
            </div>
          </div>
        </section>
      </div>

      <div className="pt-4 space-y-2.5 bg-slate-950" data-id="element-790">
        <PrimaryButton onClick={onNext} icon={<CheckCircle2Icon className="w-5 h-5" data-id="element-792" />} data-id="element-791">
          Finalize PTP
        </PrimaryButton>
        <SecondaryButton onClick={onBack} icon={<MessageSquareWarningIcon className="w-5 h-5" data-id="element-794" />} data-id="element-793">
          Review Updated PTP
        </SecondaryButton>
        <SecondaryButton onClick={() => {}} icon={<RefreshCwIcon className="w-5 h-5" data-id="element-796" />} data-id="element-795">
          Repeat Safety Quiz
        </SecondaryButton>
      </div>
    </div>;
}